/**
 * raw_codec — which pre-transcoded pack variant this GPU can sample
 * (settlement LOD0 research L5, 2026-09-03). Pure choice + a small feature
 * probe; the asset module maps pack members to `raw/<codec>/<member>`.
 */

export type RawCodec = 'bc7' | 'astc';

export interface CodecSupport { bc: boolean; astc: boolean }

/** Desktop GPUs take BC7; mobile and Apple take ASTC 4×4; nothing raw when neither (the Basis files stay). */
export function pickRawCodec(s: CodecSupport, optOut = false): RawCodec | null {
  if (optOut) return null;
  if (s.bc) return 'bc7';
  if (s.astc) return 'astc';
  return null;
}

/** The raw twin of a store key for a codec. */
export function rawMemberUri(codec: RawCodec, memberUri: string): string {
  return `raw/${codec}/${memberUri}`;
}

let optOut: boolean | null = null;
export function rawCodecOptedOut(): boolean {
  if (optOut !== null) return optOut;
  try {
    optOut = (typeof localStorage !== 'undefined' && localStorage.getItem('atlas:raw-textures') === 'off')
      || (typeof location !== 'undefined' && /[?&]rawTextures=off\b/.test(location.search));
  } catch { optOut = false; }
  return optOut;
}

/** Probe the renderer: WebGPU device features, else the WebGL2 extensions. */
export function detectCodecSupport(renderer: unknown): CodecSupport {
  const r = renderer as { backend?: { isWebGPUBackend?: boolean; device?: { features?: { has(f: string): boolean } }; gl?: WebGL2RenderingContext } } | null | undefined;
  const feats = r?.backend?.device?.features;
  if (r?.backend?.isWebGPUBackend && feats) return { bc: feats.has('texture-compression-bc'), astc: feats.has('texture-compression-astc') };
  const gl = r?.backend?.gl ?? (r as { getContext?: () => WebGL2RenderingContext } | null | undefined)?.getContext?.();
  if (gl && typeof gl.getExtension === 'function') {
    return { bc: !!gl.getExtension('EXT_texture_compression_bptc'), astc: !!gl.getExtension('WEBGL_compressed_texture_astc') };
  }
  return { bc: false, astc: false };
}
